import type React from "react";
import {useMemo, useState} from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import {CircularProgress, Paper, Skeleton, Stack, Typography} from "@mui/material";
import {COLORS} from "~/style/colors-ts";
import {useDebouncedSearch} from "~/hooks/useDebouncedSearch";
import {useSupplierRiskSignalsInfiniteQuery} from "~/pages/supplier-360/tabs/SupplierInsights/hooks/useSupplierRiskSignalsInfiniteQuery";
import {
    EMPTY_SIGNAL_FEED_FILTERS,
    SIGNAL_FEED_PAGE_SIZE,
    type SignalFeedFilterState,
} from "~/pages/supplier-360/tabs/SupplierInsights/signalFeed";
import {SUPPLIER_DETAIL_SCROLL_ID} from "~/pages/supplier-360/constants/supplierDetailScroll";
import {SignalFeedFilters} from "./SignalFeedFilters";
import {SignalFeedCard} from "./SignalFeedCard";

/** Load the next page when the list is scrolled to within this distance of its end. */
const SCROLL_THRESHOLD = '200px';

export const SignalFeedSection: React.FC<{entityId: number}> = ({entityId}) => {
    const [filters, setFilters] = useState<SignalFeedFilterState>(EMPTY_SIGNAL_FEED_FILTERS);
    const search = useDebouncedSearch();

    const updateFilters = (next: Partial<SignalFeedFilterState>) => setFilters(prev => ({...prev, ...next}));
    const reset = () => {
        search.clear();
        setFilters(EMPTY_SIGNAL_FEED_FILTERS);
    };

    const {data, isLoading, isSuccess, isError, hasNextPage, isFetchingNextPage, fetchNextPage} = useSupplierRiskSignalsInfiniteQuery(
        entityId,
        {
            impact_type: filters.impactTypes,
            category: filters.categories,
            language: filters.languages,
            search: search.debounced || undefined,
        },
        SIGNAL_FEED_PAGE_SIZE,
    );
    const rows = useMemo(() => data?.pages.flatMap(p => p.results) ?? [], [data]);
    const total = data?.pages[0]?.count ?? 0;
    const isFiltered = filters.impactTypes.length > 0 || filters.categories.length > 0
        || filters.languages.length > 0 || !!search.debounced;

    // Once the feed has loaded, the real filter bar stays put — a filter change refetches, and
    // swapping the bar for a skeleton then would close an open dropdown or drop search focus.
    // Seeded during render, not in an effect (see AGENTS.md "Effects and state").
    const [hasLoaded, setHasLoaded] = useState(false);
    if (isSuccess && !hasLoaded) setHasLoaded(true);

    const loadMore = () => {
        if (!isFetchingNextPage) void fetchNextPage();
    };

    return (
        <>
            <Typography variant="h6" fontWeight={600} fontSize={15} sx={{mt:3, mb:2}}>Signal feed</Typography>

            {hasLoaded || isError
                ? (
                    <SignalFeedFilters
                        filters={filters}
                        onFiltersChange={updateFilters}
                        searchInput={search.input}
                        onSearchInput={search.setInput}
                        onReset={reset}
                    />
                )
                : (
                    <Stack direction="row" flexWrap="wrap" alignItems="center" gap={1.5} sx={{mb: 2}}>
                        <Skeleton variant="rounded" sx={{width: 200, maxWidth: 200}} height={40}/>
                        <Skeleton variant="rounded" sx={{width: 240, maxWidth: 200}} height={40}/>
                        <Skeleton variant="rounded" sx={{width: 170, maxWidth: 200}} height={40}/>
                        <Skeleton variant="rounded" sx={{width: 240, maxWidth: 200}} height={40}/>
                        <Skeleton variant="rectangular" width={64} height={30}/>
                    </Stack>
                )}

            {isLoading && (
                <Stack gap={1.5}>
                    <Skeleton variant="rounded" height={110}/>
                    <Skeleton variant="rounded" height={110}/>
                    <Skeleton variant="rounded" height={110}/>
                </Stack>
            )}

            {isError && (
                <Typography variant="body2" color="text.secondary">Signals could not be loaded.</Typography>
            )}

            {data && rows.length > 0 && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{mb: 1.5}}>
                    Showing <b>{rows.length.toLocaleString('en-GB')}</b> of {total.toLocaleString('en-GB')} scored articles on this supplier
                </Typography>
            )}


            {data && rows.length === 0 && (
                <Paper variant="outlined" sx={{p: 3, textAlign: 'center', borderColor: COLORS.grey[100], borderRadius: 1.5}}>
                    <Typography variant="body2" color="text.secondary">
                        {isFiltered ? 'No articles match these filters.' : 'No articles found.'}
                    </Typography>
                </Paper>
            )}

            {rows.length > 0 && (
                <InfiniteScroll
                    scrollableTarget={SUPPLIER_DETAIL_SCROLL_ID}
                    dataLength={rows.length}
                    next={loadMore}
                    hasMore={Boolean(hasNextPage)}
                    // The list is one <Stack>, not an array of children, so without this the
                    // library treats it as empty and shows the loader permanently
                    hasChildren={rows.length > 0}
                    loader={
                        <Stack alignItems="center" sx={{py: 2}}>
                            <Skeleton variant="rounded" width="100%" height={110}/>
                        </Stack>
                    }
                    scrollThreshold={SCROLL_THRESHOLD}
                    style={{overflow: 'visible'}}
                >
                    <Stack gap={1.5}>
                        {rows.map(r => <SignalFeedCard key={r.id} article={r}/>)}
                    </Stack>
                </InfiniteScroll>
            )}
        </>
    );
};
